import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { isDev, isDevStage } from "@/config/configuration";
import {
  ACCENT_SCHEMES,
  DARK_BASE_SCHEMES,
  LIGHT_BASE_SCHEMES,
  THEME_MODE_OPTIONS,
  type AccentSchemeDefinition,
  type BaseSchemeDefinition,
} from "@/modules/user-settings/_constants/themes";
import { Language, userConfigStore, type AccentScheme, type BaseScheme } from "@/store/user-config-store";
import { Check, EyeClosed, EyeIcon, Languages, Monitor, Moon, Sun } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

const LANGUAGE_OPTIONS = [
  { language: Language.German, labelKey: "user-settings:LanguageGerman" },
  { language: Language.English, labelKey: "user-settings:LanguageEnglish" },
] as const;

const handleSelect = (e: Event, action: () => void): void => {
  e.preventDefault();
  action();
};

interface SchemeMenuItemProps {
  color: string;
  label: string;
  isSelected: boolean;
  onSelect: (e: Event) => void;
}

function SchemeMenuItem({ color, label, isSelected, onSelect }: SchemeMenuItemProps) {
  return (
    <DropdownMenuItem onSelect={onSelect}>
      <span className="border-border size-3.5 rounded-full border" style={{ backgroundColor: color }} />
      <span>{label}</span>
      {isSelected && <Check className="ml-auto h-4 w-4" />}
    </DropdownMenuItem>
  );
}

interface ColorSchemeSubmenuProps {
  icon: ReactNode;
  label: string;
  baseSchemes: BaseSchemeDefinition[];
  selectedBaseScheme: BaseScheme;
  onSelectBaseScheme: (scheme: BaseScheme) => void;
  selectedAccentScheme: AccentScheme;
  onSelectAccentScheme: (scheme: AccentScheme) => void;
  getAccentColor: (scheme: AccentSchemeDefinition) => string;
}

function ColorSchemeSubmenu({
  icon,
  label,
  baseSchemes,
  selectedBaseScheme,
  onSelectBaseScheme,
  selectedAccentScheme,
  onSelectAccentScheme,
  getAccentColor,
}: ColorSchemeSubmenuProps) {
  const { t } = useTranslation();

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {icon}
        <span>{label}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuLabel>{t("user-settings:BaseScheme")}</DropdownMenuLabel>
        {baseSchemes.map((s) => (
          <SchemeMenuItem
            key={s.scheme}
            color={s.backgroundColor}
            label={t(`user-settings:${s.labelKey}`)}
            isSelected={selectedBaseScheme === s.scheme}
            onSelect={(e) => handleSelect(e, () => onSelectBaseScheme(s.scheme))}
          />
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t("user-settings:AccentScheme")}</DropdownMenuLabel>
        {ACCENT_SCHEMES.map((s) => (
          <SchemeMenuItem
            key={s.scheme}
            color={getAccentColor(s)}
            label={t(`user-settings:${s.labelKey}`)}
            isSelected={selectedAccentScheme === s.scheme}
            onSelect={(e) => handleSelect(e, () => onSelectAccentScheme(s.scheme))}
          />
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

/**
 * Dev/dev-stage-only appearance and language shortcuts for the user menu.
 * Renders nothing in production; the full settings live in the user-settings module.
 */
export function AppearanceMenuItems() {
  const { t } = useTranslation();
  const {
    theme,
    setTheme,
    language,
    setLanguage,
    showTanstackQueryButton,
    setShowTanstackQueryButton,
    lightBaseScheme,
    setLightBaseScheme,
    darkBaseScheme,
    setDarkBaseScheme,
    lightAccentScheme,
    setLightAccentScheme,
    darkAccentScheme,
    setDarkAccentScheme,
  } = userConfigStore();

  if (!isDev && !isDevStage) {
    return null;
  }

  const ActiveThemeModeIcon = THEME_MODE_OPTIONS.find((option) => option.theme === theme)?.Icon ?? Monitor;

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <ActiveThemeModeIcon />
          <span>{t("user-settings:ThemeMode")}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {THEME_MODE_OPTIONS.map(({ theme: themeMode, labelKey, Icon }) => (
            <DropdownMenuItem key={themeMode} onSelect={(e) => handleSelect(e, () => setTheme(themeMode))}>
              <Icon />
              <span>{t(labelKey)}</span>
              {theme === themeMode && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <ColorSchemeSubmenu
        icon={<Sun />}
        label={t("user-settings:LightTheme")}
        baseSchemes={LIGHT_BASE_SCHEMES}
        selectedBaseScheme={lightBaseScheme}
        onSelectBaseScheme={setLightBaseScheme}
        selectedAccentScheme={lightAccentScheme}
        onSelectAccentScheme={setLightAccentScheme}
        getAccentColor={(s) => s.primaryColor}
      />
      <ColorSchemeSubmenu
        icon={<Moon />}
        label={t("user-settings:DarkTheme")}
        baseSchemes={DARK_BASE_SCHEMES}
        selectedBaseScheme={darkBaseScheme}
        onSelectBaseScheme={setDarkBaseScheme}
        selectedAccentScheme={darkAccentScheme}
        onSelectAccentScheme={setDarkAccentScheme}
        getAccentColor={(s) => s.primaryColorDark ?? s.primaryColor}
      />
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Languages />
          <span>{t("user-settings:Language")}</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {LANGUAGE_OPTIONS.map(({ language: languageOption, labelKey }) => (
            <DropdownMenuItem key={languageOption} onSelect={(e) => handleSelect(e, () => setLanguage(languageOption))}>
              <span>{t(labelKey)}</span>
              {language === languageOption && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      {isDev && (
        <DropdownMenuItem onSelect={(e) => handleSelect(e, () => setShowTanstackQueryButton(!showTanstackQueryButton))}>
          {showTanstackQueryButton ? <EyeIcon /> : <EyeClosed />}
          <p>{t("TanstackQuery")}</p>
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
    </>
  );
}
